import Vue from 'vue';
import Router from 'vue-router';
import * as firebase from 'firebase/app';
import 'firebase/auth';
import SignUpView from '@/views/SignUp.view.vue';
import LogInView from '@/views/LogIn.view.vue';
import DashboardView from '@/views/Dashboard.view.vue';
import EditUserProfileView from '@/views/EditUserProfile.view.vue';
import TeamDashboardView from '@/views/TeamDashboard.view.vue';
import CreateTeamView from '@/views/CreateTeam.view.vue';
import EditTeamView from '@/views/EditTeam.view.vue';
import OnboardingView from '@/views/Onboarding.view.vue';
import UsersView from '@/views/Users.view.vue';
import TeamsView from '@/views/Teams.view.vue';
import InviteUserView from '@/views/InviteUser.view.vue';
import TeamStandupView from '@/views/TeamStandup.view.vue';
import WorkspaceSettingsView from '@/views/WorkspaceSettings.view.vue';
import SlackRedirectView from '@/views/SlackRedirect.view.vue';
import SlackMeetingReminderView from '@/views/SlackMeetingReminder.view.vue';
import SlackLinkUserView from '@/views/SlackLinkUser.view.vue';
import AnalyticsView from '@/views/Analytics.view.vue';
import ResetPasswordView from '@/views/ResetPassword.view.vue';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      redirect: '/log-in',
      name: 'home',
      component: LogInView,
      meta: {
        hasSideNavigation: false,
        requiresAuth: false,
      },
    },
    {
      path: '/sign-up/:invitationId?',
      name: 'Sign Up for Skwad',
      component: SignUpView,
      meta: {
        hasSideNavigation: false,
        requiresAuth: false,
      },
    },
    {
      path: '/log-in',
      name: 'Log In',
      component: LogInView,
      meta: {
        hasSideNavigation: false,
        requiresAuth: false,
      },
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: DashboardView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/users/',
      name: 'Workspace Users',
      component: UsersView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/users/:id',
      name: 'User Dashboard',
      component: DashboardView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/users/edit-profile/:id',
      name: 'Edit User Profile',
      component: EditUserProfileView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/invite-user',
      name: 'Invite User',
      component: InviteUserView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/create-team',
      name: 'Create Team',
      component: CreateTeamView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/my-profile',
      name: 'Edit My Profile',
      component: EditUserProfileView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/teams/',
      name: 'Workspace Teams',
      component: TeamsView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/teams/:id',
      name: 'Team Dashboard',
      component: TeamDashboardView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/teams/edit/:teamId',
      name: 'Edit Team',
      component: EditTeamView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/onboarding/:currentStep?',
      name: 'Workspace Onboarding',
      component: OnboardingView,
      meta: {
        hasSideNavigation: false,
        requiresAuth: true,
      },
    },
    {
      path: '/standup/:standupId',
      name: 'Team Standup',
      component: TeamStandupView,
      meta: {
        hasSideNavigation: false,
        requiresAuth: true,
      },
    },
    {
      path: '/workspace/settings',
      name: 'Workspace Settings',
      component: WorkspaceSettingsView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/slack/incoming',
      name: 'Slack Redirect',
      component: SlackRedirectView,
      props: route => ({ code: route.query.code, state: route.query.state }),
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/slack/link-user',
      name: 'Slack Link SUP User',
      component: SlackLinkUserView,
      props: route => ({
        sid: route.query.sid,
        ts: route.query.ts,
        channel: route.query.channel,
      }),
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/slack/launch-startup',
      name: 'Slack Meeting Reminder',
      component: SlackMeetingReminderView,
      props: route => ({ teamId: route.query.team_id }),
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/analytics',
      name: 'Analytics',
      component: AnalyticsView,
      meta: {
        hasSideNavigation: true,
        requiresAuth: true,
      },
    },
    {
      path: '/reset-password',
      name: 'Reset Password',
      component: ResetPasswordView,
      meta: {
        hasSideNavigation: false,
        requiresAuth: false,
      },
    },
    {
      path: '/auth-actions',
      redirect: (to) => {
        const { query } = to;
        if (query.mode === 'verifyEmail') {
          return { path: '/verify-email', query };
        }
        if (query.mode === 'resetPassword') {
          return { path: '/reset-password', query };
        }
        return { path: '/log-in' };
      },
      meta: {
        hasSideNavigation: false,
        requiresAuth: false,
      },
    },
  ],
});

function routerPathForAuthenticatedUsers(to, from, next) {
  if (to.path === '/log-in' || to.path === '/sign-up') next('/dashboard');
  else next();
}

function routerPathForUnauthenticatedUsers(to, from, next) {
  if (!to.meta.requiresAuth) next();
  else next('/log-in');
}

router.beforeEach((to, from, next) => {
  if (firebase.auth().currentUser) routerPathForAuthenticatedUsers(to, from, next);
  else routerPathForUnauthenticatedUsers(to, from, next);
});

export default router;
